import { useEffect, useRef, useState, useCallback } from "react";
import type { Ticker } from "../types/api";

interface UseWebSocketOptions {
  symbol: string;
  enabled?: boolean;
  onMessage?: (data: Ticker) => void;
}

/**
 * Hook pour gérer les connexions WebSocket pour les données de marché en temps réel
 */
export const useWebSocket = ({
  symbol,
  enabled = true,
  onMessage,
}: UseWebSocketOptions) => {
  const [ticker, setTicker] = useState<Ticker | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const onMessageRef = useRef(onMessage);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;

  // Mettre à jour la référence du callback sans déclencher de reconnexion
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  // Normaliser le symbole une seule fois
  const normalizeSymbol = useCallback((sym: string): string => {
    let normalized = sym.replace("/", "").toLowerCase();
    if (normalized.endsWith("usd") && normalized !== "usdt") {
      normalized = normalized.replace("usd", "usdt");
    }
    return normalized;
  }, []);

  useEffect(() => {
    if (!enabled || !symbol) {
      // Fermer la connexion si désactivée
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      setIsConnected(false);
      return;
    }

    // Nettoyer les tentatives de reconnexion précédentes
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Fermer la connexion existante si le symbole change
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    const normalized = normalizeSymbol(symbol);
    const wsUrl = `wss://stream.binance.com:9443/ws/${normalized}@ticker`;

    const connect = () => {
      try {
        const ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          setIsConnected(true);
          reconnectAttemptsRef.current = 0;
          console.log(`WebSocket connecté pour ${symbol}`);
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            const tickerData: Ticker = {
              symbol: data.s,
              price: parseFloat(data.c),
              change24h: parseFloat(data.p),
              changePercent24h: parseFloat(data.P),
              high24h: parseFloat(data.h),
              low24h: parseFloat(data.l),
              volume24h: parseFloat(data.v),
              lastUpdate: Date.now(),
            };
            setTicker(tickerData);
            onMessageRef.current?.(tickerData);
          } catch (error) {
            console.error("Erreur lors du parsing des données WebSocket:", error);
          }
        };

        ws.onerror = (error) => {
          console.error("Erreur WebSocket:", error);
          setIsConnected(false);
        };

        ws.onclose = (event) => {
          setIsConnected(false);
          console.log(`WebSocket fermé pour ${symbol}`, event.code, event.reason);

          // Reconnexion automatique seulement si ce n'est pas une fermeture intentionnelle
          if (event.code !== 1000 && enabled && reconnectAttemptsRef.current < maxReconnectAttempts) {
            reconnectAttemptsRef.current += 1;
            const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
            console.log(`Tentative de reconnexion ${reconnectAttemptsRef.current}/${maxReconnectAttempts} dans ${delay}ms`);
            
            reconnectTimeoutRef.current = setTimeout(() => {
              connect();
            }, delay);
          }
        };

        wsRef.current = ws;
      } catch (error) {
        console.error("Erreur lors de la création du WebSocket:", error);
        setIsConnected(false);
      }
    };

    connect();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close(1000, "Component unmount");
        wsRef.current = null;
      }
      setIsConnected(false);
    };
  }, [symbol, enabled, normalizeSymbol]);

  return { ticker, isConnected };
};

